import { createContext, useEffect, useState, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  password_change_required?: boolean;
}

interface UserData {
  id: string;
  email?: string;
}

interface SessionData {
  user: UserData;
  access_token: string;
}

interface AuthContextType {
  user: UserData | null;
  profile: Profile | null;
  session: SessionData | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  changePassword: (newPassword: string) => Promise<{ error: Error | null }>;
}

// eslint-disable-next-line react-refresh/only-export-components -- context must be exported for useAuth hook
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserData | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      logger.error('Failed to fetch profile:', error);
      return;
    }
    if (data) {
      setProfile(data);
      // Set Sentry user context for error tracking
      Sentry.setUser({
        id: userId,
        email: data.email,
        username: `${data.first_name} ${data.last_name}`,
      });
    }
  };

  useEffect(() => {
    // Get initial session
    void supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setSession(session as unknown as SessionData);
        setUser({ id: session.user.id, email: session.user.email });
        void fetchProfile(session.user.id);
      }
      setLoading(false);
    }).catch((error: unknown) => {
      logger.error('Failed to get session:', error);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session) {
          setSession(session as unknown as SessionData);
          setUser({ id: session.user.id, email: session.user.email });
          void fetchProfile(session.user.id);
        } else {
          setSession(null);
          setUser(null);
          setProfile(null);
          // Clear Sentry user context on logout
          Sentry.setUser(null);
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setSession(null);
    // Clear Sentry user context on logout
    Sentry.setUser(null);
  };

  const changePassword = async (newPassword: string) => {
    const { error: authError } = await supabase.auth.updateUser({ password: newPassword });
    if (authError) return { error: authError };

    const { error: profileError } = await supabase
      .from('profiles')
      .update({ password_change_required: false })
      .eq('id', user?.id);

    if (!profileError) {
      setProfile(prev => prev ? { ...prev, password_change_required: false } : null);
    }

    return { error: profileError };
  };

  return (
    <AuthContext.Provider value={{ user, profile, session, loading, signIn, signOut, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
}
